import styles from './ProviderOptions.module.css';
import cn from 'classnames';
import { creatorbase } from '@round/api';
import { useClientProviderFeeConfigs } from '@round/creatorbase-helpers/api-hooks';
import { Skeleton } from '@round/ui-kit';
import { Provider } from '../helpers';
import { assets } from '@round/whitelabel-ui';

const { PayPal: PaypalLogo, EllipsisCircle: OtherIcon, UserGroup: UserGroupIcon, TipaltiLogo } = assets;

const ProviderLogos: Record<Provider, React.ReactNode> = {
    PAYPAL: <PaypalLogo className={styles.logo} />,
    TIPALTI: <TipaltiLogo className={styles.logo} />,
    OTHER: (
        <div className={styles.otherLogo}>
            <OtherIcon className={styles.otherIcon} />
            <span className={styles.otherLabel}>Other</span>
        </div>
    ),
    CREATOR_AGENCY: (
        <div className={styles.otherLogo}>
            <UserGroupIcon className={styles.otherIcon} />
            <span className={styles.otherLabel}>Agency</span>
        </div>
    ),
};

type Props = {
    selectedValue: Provider | null | undefined;
    onChange: (value: Provider | null) => void;
    clientId: number | undefined;
    isDisabled?: boolean;
    allowedProviders?: Provider[];
};

const getProviderOptionFromFees = (provider: Provider, fees: creatorbase.ClientProviderFeeConfig[] | undefined) => ({
    feeConfigId: fees?.find((fee) => fee.provider_name === provider)?.provider_fee_config_id || null,
    name: provider,
    logo: ProviderLogos[provider],
});

const ProviderOptions = ({ selectedValue, onChange, clientId, isDisabled, allowedProviders }: Props) => {
    const { data: feeConfigs, isLoading: areFeeConfigsLoading } = useClientProviderFeeConfigs({ client_id: clientId });
    // We only expect one fee config per provider right now, so we will map the first one we find to each option.
    const options = [
        getProviderOptionFromFees('TIPALTI', feeConfigs?.results),
        getProviderOptionFromFees('PAYPAL', feeConfigs?.results),
        getProviderOptionFromFees('OTHER', feeConfigs?.results),
        getProviderOptionFromFees('CREATOR_AGENCY', feeConfigs?.results),
    ].filter((provider) => !allowedProviders || allowedProviders.includes(provider.name));

    return (
        <div className={styles.container}>
            {areFeeConfigsLoading ? (
                <>
                    <Skeleton containerClassName={styles.skeleton} height="100%" />
                    <Skeleton containerClassName={styles.skeleton} height="100%" />
                </>
            ) : (
                options.map((provider) => (
                    <label
                        key={provider.name}
                        htmlFor={provider.name}
                        className={cn(styles.option, {
                            [styles.selected]: provider.name === selectedValue,
                            [styles.disabled]: isDisabled || provider.feeConfigId === null,
                        })}
                    >
                        {provider.logo}
                        <input
                            id={provider.name}
                            type="radio"
                            name="provider"
                            value={provider.name || undefined}
                            checked={provider.name === selectedValue}
                            onChange={(e) => {
                                onChange(e.target.value ? (e.target.value as Provider) : null);
                            }}
                            // No fee config was found for this provider, so we disable the option.
                            // TODO: add tooltip to explain why the option is disabled.
                            disabled={isDisabled || provider.feeConfigId === null}
                        />
                    </label>
                ))
            )}
        </div>
    );
};

export default ProviderOptions;
